import type { StateCreator } from 'zustand/index';
import { current } from 'immer';
import { produceMutation } from '@epihub/web/src/zustand/produceMutation';
import type { ThemeBuilder } from './builder';

function selectThemeState<
  // The mutable props of the theme
  State extends object = object,
  // The result, when state changes Creator + State produces theme
  Theme extends object = object,
>(getState: () => ThemeStoreSlice<State, Theme>) {
  return function getTheme() {
    const state = getState();
    return {
      theme: state.theme.theme,
      state: state.theme.__state,
    };
  };
}

export type ThemeStoreSlice<
  // The mutable props of the theme
  State extends object = object,
  // The result, when state changes Creator + State produces theme
  Theme extends object = object,
> = {
  theme: {
    theme: Theme;
    __state: State;
    __builder: ThemeBuilder<any, State, Theme>;
    updateTheme: (
      mutate: (
        state: State,
        getTheme: ReturnType<typeof selectThemeState<State, Theme>>,
      ) => void,
    ) => void;
  };
};

export function createZustandThemeSlice<
  // How to create the theme
  Creator extends object = object,
  // The mutable props of the theme
  State extends object = object,
  // The result, when state changes Creator + State produces theme
  Theme extends object = object,
>(opts: {
  builder: ThemeBuilder<Creator, State, Theme>;
  initialState: State;
}) {
  const createSlice: StateCreator<ThemeStoreSlice<State, Theme>> = (set, getState) => {
    return {
      theme: {
        theme: opts.builder.produce(opts.initialState),
        __state: opts.initialState,
        __builder: opts.builder,
        updateTheme: produceMutation(
          set,
          (
            state,
            mutate: (theme: State, getTheme: ReturnType<typeof selectThemeState<State, Theme>>) => void,
          ) => {
            // Let the mutator apply changes to the theme proxy
            mutate(state.theme.__state, selectThemeState(getState));
            // Produce the theme with the new state
            state.theme.theme = opts.builder.produce(current(state.theme.__state));
          },
        ),
      },
    };
  };

  return createSlice;
}
